import assessmentsImage from "@/assets/reports/assessments.png";
import performanceImage from "@/assets/reports/performance.png";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

interface ReportTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentId: string | null;
  exportGradingReport: (assessmentId: string, type: "grades" | "performance") => Promise<void>;
  hasAssessmentDetails?: boolean;
  hasIncompleteSubmissions?: boolean;
}

interface ReportTypeButtonProps {
  type: "grades" | "performance";
  label: string;
  image: string;
  alt: string;
  isSelected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const ReportTypeButton = ({
  label,
  image,
  alt,
  isSelected,
  disabled = false,
  onClick,
}: ReportTypeButtonProps) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
        isSelected
          ? "border-brand bg-primary/5"
          : "border-muted hover:border-muted-foreground/30"
      } ${disabled ? "opacity-50 cursor-not-allowed hover:border-muted" : ""}`}
      title={
        disabled
          ? "Assessment details require at least one document submission."
          : undefined
      }
    >
      <div className="w-full h-32 mb-3 flex items-center justify-center overflow-hidden rounded">
        <img src={image} alt={alt} className="w-full h-full object-contain" />
      </div>
      <span className="font-medium text-center">{label}</span>
    </button>
  );
};

export const ReportTypeDialog = ({
  open,
  onOpenChange,
  assessmentId,
  exportGradingReport,
  hasAssessmentDetails = false,
  hasIncompleteSubmissions = false,
}: ReportTypeDialogProps) => {
  const [selectedType, setSelectedType] = useState<
    "grades" | "performance" | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedType(null);
      return;
    }

    if (!hasAssessmentDetails && selectedType === "grades") {
      setSelectedType(null);
    }
  }, [open, hasAssessmentDetails, selectedType]);

  const handleConfirm = async () => {
    if (!assessmentId || !selectedType) return;
    
    setIsLoading(true);
    try {
      await exportGradingReport(assessmentId, selectedType);
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[600px]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Select report type</DialogTitle>
          <DialogDescription>
            Choose the type of report to download.
          </DialogDescription>
        </DialogHeader>

        {hasIncompleteSubmissions && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Your report will be incomplete</AlertTitle>
            <AlertDescription>
              Some submissions have not been graded yet.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-4 py-4">
          <ReportTypeButton
            type="grades"
            label="Assessment details"
            image={assessmentsImage}
            alt="Assessment details"
            isSelected={selectedType === "grades"}
            disabled={!hasAssessmentDetails}
            onClick={() => {
              if (!hasAssessmentDetails) return;
              setSelectedType("grades");
            }}
          />

          <ReportTypeButton
            type="performance"
            label="Performance summary"
            image={performanceImage}
            alt="Performance summary"
            isSelected={selectedType === "performance"}
            onClick={() => setSelectedType("performance")}
          />
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading || !selectedType}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Download report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
